"use client";

import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Heart,
  Users,
  Award,
  TrendingUp,
  Sparkles,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchClientStats,
  fetchAboutContacts,
} from "@/store/slices/landingSlice";

const getIcon = (iconName: string | undefined, index: number) => {
  const icons = [Users, Award, TrendingUp, Heart];
  if (iconName) {
    const iconMap: { [key: string]: any } = {
      Users: Users,
      Award: Award,
      TrendingUp: TrendingUp,
      Heart: Heart,
    };
    return iconMap[iconName] || icons[index % icons.length];
  }
  return icons[index % icons.length];
};

const getColor = (colorName: string | undefined, index: number) => {
  const colors = [
    "from-[#8B31FF] to-[#8B31FF]",
    "from-[#8B31FF] to-[#8B31FF]",
    "from-[#2B00A4] to-[#8B31FF]",
    "from-[#8B31FF] to-[#2B00A4]",
  ];
  return colorName || colors[index % colors.length];
};

const AboutHeroSection = () => {
  const { mode } = useAppSelector((state) => state.theme);
  const dispatch = useAppDispatch();
  const { data: stats } = useAppSelector((state) => state.landing.clientStats);
  const { data: aboutData } = useAppSelector(
    (state) => state.landing.aboutContact,
  );
  const firstAbout = aboutData[0];
  const isDark = mode === "dark";

  React.useEffect(() => {
    dispatch(fetchClientStats());
    dispatch(fetchAboutContacts());
  }, [dispatch]);

  const heroStats = stats.slice(0, 4);

  return (
    <section
      className={`relative min-h-[92vh] overflow-hidden pt-24 transition-colors duration-700 ${
        isDark
          ? "bg-[#0B0F19]"
          : "bg-gradient-to-b from-white via-slate-50 to-white"
      }`}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${isDark ? "opacity-[0.05]" : "opacity-[0.04]"}`}
          style={{
            backgroundImage: isDark
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />

        <motion.div
          className={`absolute -left-24 top-8 h-[420px] w-[420px] rounded-full blur-[110px] ${isDark ? "bg-[#8B31FF]/25" : "bg-[#8B31FF]/12"}`}
          animate={{ x: [0, 70, 0], y: [0, 25, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute -right-20 bottom-0 h-[460px] w-[460px] rounded-full blur-[120px] ${isDark ? "bg-[#8B31FF]/18" : "bg-[#8B31FF]/10"}`}
          animate={{ x: [0, -80, 0], y: [0, 30, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl"
        >
          <div
            className={`mb-7 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 backdrop-blur-md ${
              isDark
                ? "border-[#8B31FF]/40 bg-[#8B31FF]/10"
                : "border-[#8B31FF]/20 bg-white/80"
            }`}
          >
            <Sparkles
              className={`h-4 w-4 ${isDark ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
            />
            <span
              className={`text-xs font-semibold uppercase tracking-[0.16em] ${isDark ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
            >
              About Hubmicroo
            </span>
          </div>

          <h1
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.12] ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {firstAbout?.mainTitle || "Innovating the Digital Future"}
          </h1>

          <p
            className={`mb-9 max-w-xl text-lg leading-relaxed sm:text-xl ${isDark ? "text-gray-300" : "text-gray-600"}`}
          >
            {firstAbout?.subtitle ||
              "We are a team of passionate innovators, designers, and developers committed to transforming ideas into exceptional digital experiences."}
          </p>

          <motion.a
            href={firstAbout?.ctaButtons?.[0]?.href || "/contact"}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-2 rounded-lg font-semibold bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_12px_35px_rgba(79,17,194,0.35)] transition-all"
          >
            {firstAbout?.ctaButtons?.[0]?.label || "Join Our Team"}
            <ArrowRight className="h-4 w-4" />
          </motion.a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="grid grid-cols-1 gap-5 sm:grid-cols-2"
        >
          {(heroStats.length > 0
            ? heroStats
            : [
                {
                  value: "100+",
                  label: "Projects Completed",
                  icon: "Briefcase",
                  color: "from-[#8B31FF] to-[#8B31FF]",
                },
                {
                  value: "98%",
                  label: "Success Rate",
                  icon: "TrendingUp",
                  color: "from-[#8B31FF] to-[#8B31FF]",
                },
                {
                  value: "5+",
                  label: "Industry Awards",
                  icon: "Award",
                  color: "from-[#2B00A4] to-[#8B31FF]",
                },
                {
                  value: "20+",
                  label: "Team Experts",
                  icon: "Users",
                  color: "from-[#8B31FF] to-[#2B00A4]",
                },
              ]
          ).map((stat: any, index: number) => {
            const Icon = getIcon(stat?.icon, index);
            const colorClass = getColor(stat?.color, index);

            return (
              <motion.div
                key={index}
                whileHover={{ y: -8 }}
                className={`group relative overflow-hidden rounded-lg border p-6 backdrop-blur-md transition-all duration-500 ${
                  isDark
                    ? "border-white/10 bg-white/5 hover:border-[#8B31FF]/40"
                    : "border-gray-200 bg-white/80 shadow-sm hover:border-[#8B31FF]/30"
                }`}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${colorClass} opacity-0 transition-opacity duration-500 group-hover:opacity-15`}
                />

                <div
                  className={`relative mb-4 inline-flex rounded-lg bg-gradient-to-br ${colorClass} p-3 shadow-lg`}
                >
                  <Icon className="h-6 w-6 text-white" />
                </div>

                <h3
                  className={`relative mb-1 text-3xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {stat?.value || "0"}
                </h3>
                <p
                  className={`relative text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}
                >
                  {stat?.label || "Metric"}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      <div
        className={`absolute bottom-0 left-0 right-0 h-px ${isDark ? "bg-white/10" : "bg-gray-200"}`}
      />
    </section>
  );
};

export default AboutHeroSection;
